// The platform limits a toolset's manifest and answers are checked against,
// which the Tool SDK checks too. src/config.ts re-exports them.

/**
 * The lowest dispatch threshold a hub manifest may set. A hub's manifest can
 * raise its threshold, but not below this floor: checkManifest reports a
 * `dispatchThreshold` under it, and staging rejects the toolset. With the
 * floor at 0.1, the hub-selection experiment dispatched none of 13 off-topic
 * questions; the highest off-topic hub score was 0.03.
 */
export const DISPATCH_THRESHOLD_FLOOR = 0.1;

/** The dispatch threshold of a hub whose manifest sets no `dispatchThreshold`. */
export const DEFAULT_DISPATCH_THRESHOLD = 0.1;

/**
 * The domains a hub's tools can fetch from, through the EgressGateway, when
 * the hub declares no egress allowlist. A manifest that declares one replaces
 * this list rather than adding to it.
 */
export const DEFAULT_EGRESS_ALLOW = ["artifacts.warmhub.dev"];

/**
 * The most accounts one host may have, besides "default": as many a
 * manifest's `egress.accounts` may declare for a host, and a hub may hold
 * API keys for. See Design > Names and definitions, "Account".
 */
export const API_ACCOUNTS_PER_HOST = 8;

/**
 * An account's name: lowercase letters, digits, `_` and `-`, 1 to 64
 * characters, starting with a letter or digit. The Curator API contract
 * checks the same pattern (`ApiKeyAccount`).
 */
export const ACCOUNT_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The most members a Set or List in an answer may hold (Design > Answer
 * shapes, rule 8). validateAnswer reports a larger collection.
 */
export const MAX_COLLECTION_MEMBERS = 1000;

/**
 * The largest tool bundle staging accepts, in bytes: 5 MiB. `npm run build`
 * fails on a larger bundle before you push it.
 */
export const MAX_BUNDLE_BYTES = 5 * 1024 * 1024;

// The staging caps, which bound what one push costs: a staged toolset is a D1
// row holding its manifest, its bundles fill KV, and staging writes an intent
// index row, with an embedding, for each example. checkManifest and
// checkToolsetBytes report a toolset over one, so `npm run build` fails on it
// before the push. Values confirmed by Jeff, 2026-10-14 (#302). See Design >
// Toolsets and tools, "Staging checks".

/** The most bytes a toolset's bundles may hold together: 20 MiB. */
export const MAX_TOOLSET_BUNDLE_BYTES = 20 * 1024 * 1024;

/** The largest manifest.json staging accepts, in UTF-8 bytes: 256 KiB. */
export const MAX_MANIFEST_BYTES = 256 * 1024;

/** The most intents a toolset may declare. */
export const MAX_INTENTS = 50;

/** The most example questions one intent may list. */
export const MAX_EXAMPLES_PER_INTENT = 20;

/** The most golden questions a toolset may hold. */
export const MAX_GOLDEN_QUESTIONS = 200;
